export type RgbColor = {
  r: number
  g: number
  b: number
}

export type HslColor = {
  h: number
  s: number
  l: number
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1)
}

export function normalizeHue(hue: number): number {
  return ((hue % 360) + 360) % 360
}

export function shortestHueDelta(from: number, to: number): number {
  const delta = normalizeHue(to - from)
  return delta > 180 ? delta - 360 : delta
}

export function hueDistance(first: number, second: number): number {
  return Math.abs(shortestHueDelta(second, first))
}

export function normalizeHexNumber(hex: number): number {
  return (hex >>> 0) & 0xffffff
}

export function hexToHsl(hex: number): HslColor {
  return rgbToHsl(hexNumberToRgb(hex))
}

export function hslToHex(h: number, s: number, l: number): number {
  return rgbToHexNumber(hslToRgb(h, s, l))
}

export function hslToRgb(h: number, s: number, l: number): RgbColor {
  const normalizedHue = normalizeHue(h)
  const normalizedSaturation = clamp(s, 0, 100) / 100
  const normalizedLightness = clamp(l, 0, 100) / 100
  const k = (n: number) => (n + normalizedHue / 30) % 12
  const a = normalizedSaturation * Math.min(normalizedLightness, 1 - normalizedLightness)
  const f = (n: number) => normalizedLightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))

  return {
    r: Math.round(255 * f(0)),
    g: Math.round(255 * f(8)),
    b: Math.round(255 * f(4)),
  }
}

export function rgbToHsl(rgb: RgbColor): HslColor {
  const r = clamp(rgb.r, 0, 255) / 255
  const g = clamp(rgb.g, 0, 255) / 255
  const b = clamp(rgb.b, 0, 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return { h: 0, s: 0, l: l * 100 }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0

  if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4

  return { h: h * 60, s: s * 100, l: l * 100 }
}

export function hslColorNumber(hue: number, saturation: number, lightness: number): number {
  return hslToHex(
    normalizeHue(hue),
    clamp(saturation, 0, 100),
    clamp(lightness, 8, 92),
  ) >>> 0
}

export function hslVariant(
  hsl: HslColor,
  hueDelta: number,
  saturationDelta: number,
  lightnessDelta: number,
): number {
  return hslColorNumber(hsl.h + hueDelta, hsl.s + saturationDelta, hsl.l + lightnessDelta)
}

export function hslTowardHue(
  hsl: HslColor,
  targetHue: number,
  amount: number,
  saturationDelta: number,
  lightnessDelta: number,
): number {
  return hslVariant(hsl, shortestHueDelta(hsl.h, targetHue) * amount, saturationDelta, lightnessDelta)
}

export function hexNumberToRgb(hex: number): RgbColor {
  const normalized = normalizeHexNumber(hex)
  return {
    r: (normalized >>> 16) & 0xff,
    g: (normalized >>> 8) & 0xff,
    b: normalized & 0xff,
  }
}

export function rgbToHexNumber(rgb: RgbColor): number {
  return ((clampByte(rgb.r) & 0xff) << 16) | ((clampByte(rgb.g) & 0xff) << 8) | (clampByte(rgb.b) & 0xff)
}

export function hexNumberToString(hex: number): string {
  return `#${normalizeHexNumber(hex).toString(16).padStart(6, '0')}`
}

export function hexStringToNumber(hex: string): number {
  const rgb = parseHexColor(hex)
  return rgbToHexNumber(rgb)
}

export function coerceHexColorNumber(input: string | number | null | undefined): number | undefined {
  if (input == null) return undefined
  if (typeof input === 'number') return Number.isFinite(input) ? normalizeHexNumber(input) : undefined

  const trimmed = input.trim()
  if (!trimmed) return undefined
  const raw = trimmed.startsWith('#')
    ? trimmed.slice(1)
    : trimmed.startsWith('0x')
      ? trimmed.slice(2)
      : trimmed
  const full = raw.length === 3 ? raw.split('').map((channel) => channel + channel).join('') : raw
  if (!/^[0-9a-f]{6}$/i.test(full)) return undefined

  return Number.parseInt(full, 16)
}

export function hexStringToHsl(hex: string): HslColor {
  return rgbToHsl(parseHexColor(hex))
}

export function parseHexColor(hex: string): RgbColor {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) throw new Error(`Expected a 6-digit hex color, got "${hex}"`)
  const value = Number.parseInt(match[1], 16)

  return hexNumberToRgb(value)
}

export function rgbToHexString(rgb: RgbColor): string {
  return hexNumberToString(rgbToHexNumber(rgb))
}

export function mixHexColors(from: string, to: string, amount: number): string {
  const fromRgb = parseHexColor(from)
  const toRgb = parseHexColor(to)
  const clampedAmount = clamp01(amount)
  return rgbToHexString({
    r: Math.round(fromRgb.r + (toRgb.r - fromRgb.r) * clampedAmount),
    g: Math.round(fromRgb.g + (toRgb.g - fromRgb.g) * clampedAmount),
    b: Math.round(fromRgb.b + (toRgb.b - fromRgb.b) * clampedAmount),
  })
}

export function compositeHexColor(foreground: string, alpha: number, background: string): string {
  const foregroundRgb = parseHexColor(foreground)
  const backgroundRgb = parseHexColor(background)
  const amount = clamp01(alpha)

  return rgbToHexString({
    r: Math.round(foregroundRgb.r * amount + backgroundRgb.r * (1 - amount)),
    g: Math.round(foregroundRgb.g * amount + backgroundRgb.g * (1 - amount)),
    b: Math.round(foregroundRgb.b * amount + backgroundRgb.b * (1 - amount)),
  })
}

export function relativeLuminance(rgb: RgbColor): number {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const normalized = clamp(channel, 0, 255) / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(first: RgbColor, second: RgbColor): number {
  const firstLuminance = relativeLuminance(first)
  const secondLuminance = relativeLuminance(second)
  const lighter = Math.max(firstLuminance, secondLuminance)
  const darker = Math.min(firstLuminance, secondLuminance)

  return (lighter + 0.05) / (darker + 0.05)
}

export function rgbChannelSpreadPercent(color: number): number {
  const { r, g, b } = hexNumberToRgb(color)
  return ((Math.max(r, g, b) - Math.min(r, g, b)) / 255) * 100
}

export function srgbToRgb(value: number): number {
  return value ** 2.2
}

export function rgbToSrgb(value: number): number {
  return value ** (1 / 2.2)
}

export function splitRGB(hex: number): readonly [number, number, number] {
  const { r, g, b } = hexNumberToRgb(hex)
  return [r, g, b] as const
}

export function joinRGB(r: number, g: number, b: number): number {
  return rgbToHexNumber({ r, g, b })
}

export type BrushBlend = 'normal' | 'multiply' | 'screen' | 'burn'

export function blendChannel(base: number, paint: number, mode: BrushBlend, alpha: number): number {
  const baseLinear = srgbToRgb(base / 255)
  const paintLinear = srgbToRgb(paint / 255)
  let blended = paintLinear

  switch (mode) {
    case 'normal':
      blended = lerp(baseLinear, paintLinear, alpha)
      alpha = 1
      break
    case 'multiply':
      blended = baseLinear * paintLinear
      break
    case 'screen':
      blended = 1 - (1 - baseLinear) * (1 - paintLinear)
      break
    case 'burn':
      blended = 1 - Math.min(1, (1 - baseLinear) / (paintLinear === 0 ? 1e-6 : paintLinear))
      break
  }

  const out = mode === 'normal' ? blended : lerp(baseLinear, blended, alpha)
  return Math.round(clamp01(rgbToSrgb(out)) * 255)
}

export function blendColor(baseHex: number, brushHex: number, mode: BrushBlend, opacity: number): number {
  const [baseR, baseG, baseB] = splitRGB(baseHex)
  const [paintR, paintG, paintB] = splitRGB(brushHex)
  const r = blendChannel(baseR, paintR, mode, opacity)
  const g = blendChannel(baseG, paintG, mode, opacity)
  const b = blendChannel(baseB, paintB, mode, opacity)
  return joinRGB(r, g, b)
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount
}

function clampByte(value: number): number {
  return clamp(Math.round(value), 0, 255)
}
